/**
 * a class that includes helper functions for validating values against a certain type
 */
export default class ParameterValidator {

	instance;
	name;

	/**
	 * 
	 * @param {Object|String} instanceOrName a class instance (this) or string
	 */
	constructor( instanceOrName ) {

		if ( instanceOrName == undefined ) {
			throw new TypeError( `ParameterValidator :: a class instance (usually \`this\`) or name must be passed` );
		}

		if ( typeof instanceOrName === 'string' ) {
			
			this.name = instanceOrName;

		}
		else {

			this.instance = instanceOrName;
			this.name = this.instance.constructor.name;

			if ( this.name === 'Function' ) {
				this.name = this.instance.name;
			}

		}

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 */
	isDefined( key, value ) {

		if ( value == undefined ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be defined` );
		}

		return value;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {any} value a constructor reference (class name) (e.g. Array, HTMLElement)
	 * @param {Object} options optional parameters
	 */
	isInstance( key, value, constructor, { required = true } = {} ) {

		if ( required && !(value instanceof constructor) ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be an instance of \`${ constructor.name }\`` );
		}

		return value;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isObject( key, value, { required = true } = {} ) {

		if ( required && !(typeof value === 'object') ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be an object` );
		}

		return value;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isArray( key, value, { required = true } = {} ) {

		if ( required && !(Array.isArray( value )) ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be an array` );
		}

		return value;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isFunction( key, value, { required = true } = {} ) {

		if ( required && !(typeof value === 'function') ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be a function` );
		}

		return value;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isString( key, value, { required = true } = {} ) {

		if ( required && !(typeof value === 'string') ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be a string` );
		}

		return value;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isBoolean( key, value, { required = true } = {} ) {

		if ( required && !(typeof value === 'boolean') ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be a boolean` );
		}

		return value;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isNumber( key, value, { required = true } = {} ) {

		if ( required && !(Number( value ) === value) ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be an integer` );
		}

		return value;

	}
	
	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isInt( key, value, { required = true } = {} ) {

		if ( required && !(Number( value ) === value && value % 1 === 0) ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be an integer` );
		}

		return value;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isFloat( key, value, { required = true } = {} ) {

		if ( required && !(Number( value ) === value && value % 1 !== 0) ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be a float` );
		}

		return value;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isElement( key, value, { required = true, container = document } = {} ) {
		
		if ( typeof value === 'string' ) {
			value = container.querySelector( value );
		}

		if ( required && !(value instanceof HTMLElement) ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be an element or valid selector` );
		}

		return value;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isElementList( key, value, { required = true, container = document } = {} ) {

		if ( typeof value === 'string' ) {
			value = container.querySelectorAll( value );
		}

		if ( required && !(value instanceof HTMLCollection) && !(value instanceof NodeList) ) {
			throw new TypeError( `${ this.name } :: \`${ key }\` must be a list of elements or valid selector` );
		}

		return value;

	}

};